import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import uniqid from 'uniqid';
import Case from 'case';
import {
  Box, Text, Button,
} from 'grommet';
import { FormNext } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, FullHeightBox, AppAnchor, AppButton, AppRadioButton, Spinning, RadioToggleButton,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper, AuthBanner } from '@Components/Navigation';
import {
  sendPasswordResetInstructionsRequest,
  authUploadCompanyLogoRequest,
  fetchCustomerSubscriptionRequest,
  clearCustomerSubscriptionRequest,
  fetchCancellationStatus,
  initiateCompanyNotificationSettingRequest,
  clearCompanyNotificationSettingRequest,
  updateCompanyNotificationSettingRequest,
  fetchCustomerOnboardingData,
  fetchPayoutAccount,
  clearPayoutAccount,
  updatePayoutBusinessInfo,
  fetchPayoutBusinessInfo,
} from '@Actions';
import { COUNTRIES } from '@RedflagAI/node-utility/utils/constants.utils';
import { ProductFormDropdownSelect } from '@Components/Partial/Product/NewSearch';
import { paths } from '@Components/configs';
import { StyledAccountTypeOption } from '../../Partial/Onboarding/StyledOnboardingComponents';
import { StyledAccountInfoContainer, StyledStatusBadge } from './StyledAccountPage';
import { ProfileLogo } from '../../Navigation/Header/HeaderComponent';

export const Subscriptions = ({
  small,
  loading,
  loaderColor,
  textColor,
  subscriptionData = null,
  onboardingData = null,
}) => {
  if (loading) {
    return (
      <Box direction="column" pad="1rem" width={small ? '100%' : '25%'} align="center" justify="center">
        <Spinning color={loaderColor} size="large" />
      </Box>
    );
  }

  if (!loading && !subscriptionData && !onboardingData) return null;

  const dateOptions = { month: 'short', day: '2-digit', year: 'numeric' };
  const formatDate = (date) => (date ? new Date(date * 1000).toLocaleDateString('en-US', dateOptions) : null);

  // Subscription data section
  let subscriptionSection = null;
  if (subscriptionData) {
    const productData = subscriptionData?.productData;
    const subStatus = subscriptionData?.status;
    const cardData = subscriptionData?.cardData;
    const cardDisplay = cardData && `${cardData.brand} ending in ${cardData.last4}`;
    const createdDate = formatDate(subscriptionData?.createdAt);
    const periodStart = formatDate(subscriptionData?.currentPeriodStart);
    const periodEnd = formatDate(subscriptionData?.currentPeriodEnd);
    const canceledDate = formatDate(subscriptionData?.canceledDate);
    const willCancelDate = formatDate(subscriptionData?.willCancelDate);
    const canceledMessage = canceledDate && willCancelDate && `Canceled on ${canceledDate}, valid through ${willCancelDate}`;

    subscriptionSection = (
      <Box direction="column" gap="1rem" width={small ? '100%' : '50%'}>
        {canceledMessage && (
          <StyledStatusBadge background="#FCF8EF">
            <Text size={small ? '1rem' : '1.1rem'} color="var(--app-error)" weight={600}>
              {canceledMessage}
            </Text>
          </StyledStatusBadge>
        )}
        <Box direction="row" align="center" gap="1rem">
          <Text size="1.2rem" color={textColor} weight={600}>{productData.name}</Text>
          <StyledStatusBadge background={subStatus === 'active' ? '#EDFDF5' : '#FCF8EF'}>
            <Text size="1.1rem" color={subStatus === 'active' ? 'var(--app-success-dark)' : 'var(--app-error)'} weight={600}>
              {Case.capital(subscriptionData?.status)}
            </Text>
          </StyledStatusBadge>
        </Box>
        <Box direction="column" gap="0.75rem">
          <Box direction="row" gap="0.5rem" align="center">
            <FormNext size="1rem" color="var(--page-alt-highlight)" />
            <Text size="1rem" color={textColor} weight={500}>
              {`Monitoring up to ${productData?.metadata?.searchLimit} of your videos`}
            </Text>
          </Box>
          <Box direction="row" gap="0.5rem" align="center">
            <FormNext size="1rem" color="var(--page-alt-highlight)" />
            <Text size="1rem" color={textColor} weight={500}>
              Unlimited takedown requests
            </Text>
          </Box>
          <Box direction="column" gap="0.75rem" pad={{ top: '0.5rem' }}>
            <Text size="1rem" color={textColor} weight={400}>
              Customer since:&nbsp;
              {createdDate}
            </Text>
            <Text size="1rem" color={textColor} weight={400}>
              Current period:&nbsp;
              {periodStart}
              &nbsp;to&nbsp;
              {periodEnd}
            </Text>
            {cardData && (
              <Text size="1rem" color={textColor} weight={400}>
                Payment method:&nbsp;
                {cardDisplay}
              </Text>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Onboarding data section
  let onboardingSection = null;
  if (onboardingData) {
    const {
      paymentPending, paymentLink, youtubeCid, signedAgreementUrl,
    } = onboardingData || {};
    /* eslint-disable camelcase */
    const planLabels = {
      revenue_share: 'Revenue Share',
      hybrid: 'Hybrid',
    };
    const uploaderLabels = {
      self: 'Self-upload',
      redflag: 'Redflag Upload',
    };
    const planDisplay = youtubeCid?.plan ? planLabels[youtubeCid.plan] || youtubeCid.plan : '—';
    const uploaderDisplay = youtubeCid?.uploader ? uploaderLabels[youtubeCid.uploader] || youtubeCid.uploader : '—';
    /* eslint-enable camelcase */

    onboardingSection = (
      <Box direction="column" gap="1rem" width={small ? '100%' : '50%'}>
        <Box direction="row" align="center" gap="1rem">
          <Text size="1.2rem" color={textColor} weight={600}>Account Status:</Text>
          <StyledStatusBadge background={paymentPending ? '#FCF8EF' : '#EDFDF5'}>
            <Text size="1.1rem" color={paymentPending ? 'var(--app-error)' : 'var(--app-success-dark)'} weight={600}>
              {paymentPending ? 'Payment Pending' : 'Active'}
            </Text>
          </StyledStatusBadge>
        </Box>
        {paymentPending && paymentLink && (
          <Box direction="row" gap="0.5rem" align="center">
            <Text size="1rem" color={textColor} weight={500}>
              Complete your payment here:
            </Text>
            <a
              href={paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: loaderColor, textDecoration: 'underline', fontWeight: 600 }}
            >
              Payment Link
            </a>
          </Box>
        )}
        {onboardingData?.youtubeCid && (
          <Box direction="column" gap="0.75rem">
            <Text size="1rem" color={textColor} weight={400}>
              YouTube Content ID configuration:
            </Text>
            <Box direction="row" gap="0.5rem" align="center">
              <FormNext size="1rem" color="var(--page-alt-highlight)" />
              <Text size="1rem" color={textColor} weight={500}>
                Plan:
              </Text>
              <Text size="1rem" color={loaderColor} weight={600}>
                {planDisplay}
              </Text>
            </Box>
            <Box direction="row" gap="0.5rem" align="center">
              <FormNext size="1rem" color="var(--page-alt-highlight)" />
              <Text size="1rem" color={textColor} weight={500}>
                Uploader:
              </Text>
              <Text size="1rem" color={loaderColor} weight={600}>
                {uploaderDisplay}
              </Text>
            </Box>
            {signedAgreementUrl && (
              <AppAnchor href={signedAgreementUrl} target="_blank" color={loaderColor}>
                <Box direction="row" gap="0.35rem" align="center">
                  <FormNext size="1rem" color={loaderColor} />
                  <Text size="1rem" color={loaderColor} weight={500}>
                    View signed agreement
                  </Text>
                </Box>
              </AppAnchor>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Render both sections with gap if both present
  if (subscriptionSection && onboardingSection) {
    return (
      <Box direction="column" gap="1.5rem" width="100%">
        {subscriptionSection}
        {onboardingSection}
      </Box>
    );
  }
  // Render whichever is present
  return subscriptionSection || onboardingSection;
};

Subscriptions.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  loaderColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  subscriptionData: PropTypes.shape({
    status: PropTypes.string.isRequired,
    productData: PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      metadata: PropTypes.shape({
        searchLimit: PropTypes.string.isRequired,
        uid: PropTypes.string.isRequired,
        verticalUid: PropTypes.string.isRequired,
      }).isRequired,
      prices: PropTypes.arrayOf(PropTypes.shape({
        priceId: PropTypes.string.isRequired,
        currency: PropTypes.string.isRequired,
        unitAmount: PropTypes.number.isRequired,
        metadata: PropTypes.shape({
          order: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          displayPlatforms: PropTypes.string.isRequired,
          platforms: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        }).isRequired,
      })).isRequired,
    }).isRequired,
    cardData: PropTypes.shape({
      brand: PropTypes.string.isRequired,
      last4: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      expMonth: PropTypes.number.isRequired,
      expYear: PropTypes.number.isRequired,
    }),
    createdAt: PropTypes.number.isRequired,
    currentPeriodEnd: PropTypes.number.isRequired,
    currentPeriodStart: PropTypes.number.isRequired,
    canceledDate: PropTypes.number,
    willCancelDate: PropTypes.number,
  }),
  onboardingData: PropTypes.shape({
    onboardingId: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    paymentPending: PropTypes.bool.isRequired,
    paymentLink: PropTypes.string,
    signedAgreementUrl: PropTypes.string,
    youtubeCid: PropTypes.shape({
      uploader: PropTypes.string,
      plan: PropTypes.string,
    }),
  }),
};

export const SectionContainer = ({
  small,
  sectionTitle,
  sectionBg,
  textColor,
  children,
  customWidth = null,
  customGap = '1.5rem',
  ...rest
}) => {
  const borderProps = { size: '1px', style: 'solid', side: 'bottom' };
  let width = small ? '55%' : '25%';
  if (customWidth) width = customWidth;

  return (
    <Box direction="column" gap={customGap} background={sectionBg} round="10px" pad="1.5rem" {...rest}>
      <Box width={width} border={{ color: 'var(--nav-border)', ...borderProps }} style={{ minWidth: '15rem' }}>
        <Box
          flex={false}
          border={{ color: 'var(--page-url-color)', ...borderProps }}
          justify="end"
          pad={{ bottom: '0.5rem' }}
          width="fit-content"
        >
          <Text color={textColor} weight={600} size="1.25rem">
            {sectionTitle}
          </Text>
        </Box>
      </Box>
      <Box direction="column" gap="2rem">
        {children}
      </Box>
    </Box>
  );
};

SectionContainer.propTypes = {
  small: PropTypes.bool.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  sectionBg: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  customWidth: PropTypes.string,
  customGap: PropTypes.string,
};

const PageWrapper = ({
  small,
  location,
  pageBg,
  unconfiguredCompany = false,
  children,
}) => {
  if (unconfiguredCompany) {
    return (
      <FullHeightBox background={pageBg} pad={{ top: small ? '5.2rem' : '3rem' }}>
        <AuthBanner
          navCollapsed
          adjustLeftPad
          small={small}
          title="Account information"
          subTitle="Plan and profile settings"
          bannerChildren={null}
        />
        {children}
      </FullHeightBox>
    );
  }

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Account information',
        subTitle: 'Plan and profile settings',
      }}
    >
      {children}
    </AuthNavWrapper>
  );
};

PageWrapper.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  pageBg: PropTypes.string.isRequired,
  unconfiguredCompany: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const BUSINESS_TYPE_OPTIONS = [
  { key: 'creator', label: 'Individual' },
  { key: 'agency', label: 'Agency' },
];

const AccountPage = ({
  small,
  loading,
  secondaryLoading,
  location,
  companyEmail,
  companyLogo,
  companyId,
  sendResetInstructions,
  uploadCompanyLogo,
  fetchSubscription,
  fetchCancellation,
  clearSubscription,
  paymentPending,
  fetchNotificationSettings,
  clearNotificationSettings,
  updateNotificationSetting,
  fetchOnboardingData,
  fetchAccount,
  clearAccount,
  saveBusinessInfo,
  loadBusinessInfo,
  companyOnboardingId = null,
  onboardingData = null,
  notificationSettings = null,
  subscriptionData = null,
  cancellationRequestedAt = null,
  unconfiguredCompany = false,
  payoutAccount = null,
  payoutAccountLoading = false,
  flags = null,
}) => {
  const logoUploadRef = React.useRef(null);
  const [businessCountry, setBusinessCountry] = React.useState(null);
  const [businessType, setBusinessType] = React.useState(null);
  const [savedBusinessCountry, setSavedBusinessCountry] = React.useState(null);
  const [savedBusinessType, setSavedBusinessType] = React.useState(null);
  const [businessSaving, setBusinessSaving] = React.useState(false);
  const {
    theme: { page, button },
    settingsDisplayMap,
    accountPlaceholder,
    isDarkMode,
  } = useAppTheme();

  React.useEffect(() => {
    fetchSubscription(companyId);
    fetchCancellation();
    fetchNotificationSettings();
    if (companyOnboardingId && !onboardingData) fetchOnboardingData(companyOnboardingId);
    if (flags?.contentIdEnabled && !paymentPending) {
      fetchAccount();
      loadBusinessInfo().then((info) => {
        if (info?.country) {
          setBusinessCountry(info.country);
          setSavedBusinessCountry(info.country);
        }
        if (info?.businessType) {
          setBusinessType(info.businessType);
          setSavedBusinessType(info.businessType);
        }
      });
    }

    return () => {
      clearSubscription();
      clearNotificationSettings();
      clearAccount();
    };
  }, []);

  const handleSettingUpdate = (api, value) => {
    const body = { data: [{ api, value }] };
    updateNotificationSetting(body, () => fetchNotificationSettings());
  };

  const replaceWithDots = (str) => (str.replace(/[^ ]/g, '•'));
  const customerMessage = unconfiguredCompany
    ? 'Account features have not yet been activated. To activate, please reach out to our team at'
    : 'If you have questions or feedback, please reach out to our team at';
  const withNotificationSettings = notificationSettings
    && notificationSettings.length > 0
    && flags?.reviewEnabled
    && flags?.searchEnabled;
  const customerInfoLoading = Boolean(loading || secondaryLoading);
  const useToggleHighlight = isDarkMode ? button.hoverText : page.highlight;
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  return (
    <PageWrapper
      small={small}
      location={location}
      unconfiguredCompany={unconfiguredCompany}
      pageBg={usePageBg}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '2rem', horizontal: small ? '1rem' : '1.5rem' }}
      >
        <input
          hidden
          key={uniqid()}
          type="file"
          accept="image/*"
          onChange={(e) => (paymentPending ? null : uploadCompanyLogo(e.currentTarget.files[0]))}
          ref={logoUploadRef}
        />
        <SectionContainer
          small={small}
          sectionTitle="Plan"
          sectionBg={useSectionBg}
          textColor={page.primaryText}
          margin={{ bottom: '2rem' }}
        >
          <Box direction="column" gap={(!customerInfoLoading && (subscriptionData || onboardingData)) ? '1.5rem' : '0'}>
            <Subscriptions
              small={small}
              loading={customerInfoLoading}
              loaderColor={page.highlight}
              subscriptionData={subscriptionData}
              onboardingData={onboardingData}
              textColor={page.primaryText}
            />
            {(!customerInfoLoading && (subscriptionData || onboardingData)) && (
              <Box direction="column" gap="0.75rem">
                <Box direction="row" gap="0.75rem" wrap>
                  {subscriptionData && (
                    <AppAnchor path={paths.manageSubscription.replace(':id', subscriptionData?.verticalUid)}>
                      <AppButton
                        overrideHover
                        level="authSubmit"
                        bgColor={button.primaryBg}
                        color="white"
                        label="Manage subscription & payment method"
                        alignSelf="start"
                      />
                    </AppAnchor>
                  )}
                  {companyOnboardingId && (
                    <AppAnchor path={paths.planUpgrade}>
                      <AppButton
                        overrideHover
                        extraWide
                        level="authSubmit"
                        bgColor={button.primaryBg}
                        color="white"
                        label="Manage plan & add platforms"
                        alignSelf="start"
                      />
                    </AppAnchor>
                  )}
                </Box>
                {flags?.contentIdConfig && companyOnboardingId && (
                  <AppAnchor path={paths.cidCancellationRequest}>
                    <Text
                      size="0.8rem"
                      color="var(--app-error)"
                      style={{ textDecoration: 'underline' }}
                    >
                      {cancellationRequestedAt
                        ? 'Withdraw cancellation request'
                        : 'Cancel CID subscription'}
                    </Text>
                  </AppAnchor>
                )}
              </Box>
            )}
            <Text color={page.primaryText} weight={400} size="1rem">
              {customerMessage}
              {' '}
              <AppAnchor href="mailto:contact@redflagai.co" target="_blank" color={page.altHighlight}>
                contact@redflagai.co
              </AppAnchor>
            </Text>
          </Box>
        </SectionContainer>
        {flags?.contentIdEnabled && !paymentPending && (
          <SectionContainer
            small={small}
            sectionTitle="Content ID Payouts"
            sectionBg={useSectionBg}
            textColor={page.primaryText}
            margin={{ bottom: '2rem' }}
          >
            {payoutAccountLoading && !payoutAccount ? (
              <Box direction="column" pad="1rem" align="center" justify="center">
                <Spinning color={page.highlight} size="large" />
              </Box>
            ) : (
              <Box direction="column" gap="1rem">
                {!flags?.stripeAccountSetupComplete ? (
                  <Box direction="column" gap="0.75rem">
                    <StyledStatusBadge background="#FCF8EF">
                      <Text size="1.1rem" color="var(--app-error)" weight={600}>
                        Payout method not configured
                      </Text>
                    </StyledStatusBadge>
                    <Text size="1rem" color={page.primaryText}>
                      You haven&apos;t set up a payout method yet. Add one to start
                      receiving earnings from Content ID.
                    </Text>
                  </Box>
                ) : (
                  <Box direction="column" gap="0.75rem">
                    <StyledStatusBadge background="#EDFDF5">
                      <Text size="1.1rem" color="var(--app-success-dark)" weight={600}>
                        Payout method configured
                      </Text>
                    </StyledStatusBadge>
                    {payoutAccount?.email && (
                      <Text size="1rem" color={page.primaryText} weight={400}>
                        Account email:&nbsp;
                        <Text size="1rem" color={page.primaryText} weight={500}>
                          {payoutAccount.email}
                        </Text>
                      </Text>
                    )}
                  </Box>
                )}
                <Box direction="column" gap="0.5rem">
                  <AppAnchor path={paths.payoutSetup}>
                    <AppButton
                      overrideHover
                      extraWide
                      level="authSubmit"
                      bgColor={button.primaryBg}
                      color={button.primaryText}
                      label={flags?.stripeAccountSetupComplete ? 'Payout settings' : 'Set up payouts'}
                      alignSelf="start"
                    />
                  </AppAnchor>
                  {flags?.stripeAccountSetupComplete && (
                    <AppAnchor path={paths.payoutStatus}>
                      <Text size="0.85rem" color={page.altHighlight} style={{ textDecoration: 'underline' }}>
                        Manage payout account
                      </Text>
                    </AppAnchor>
                  )}
                </Box>
              </Box>
            )}
          </SectionContainer>
        )}
        {withNotificationSettings && (
          <SectionContainer
            small={small}
            sectionTitle="Notifications"
            textColor={page.primaryText}
            sectionBg={useSectionBg}
            margin={{ bottom: '2rem' }}
          >
            <Box direction="column" gap="1rem">
              <Text size="1rem" color={page.primaryText}>
                If enabled, you will recieve a weekly summary email about found results,
                takedown requests, and other important information.
              </Text>
              <Text size="1rem" color={page.primaryText}>
                Emails will be sent to
                {' '}
                <Text size="1rem" color={page.primaryText} weight={500}>
                  {companyEmail}
                </Text>
              </Text>
            </Box>
            <Box direction="column" gap="1.5rem">
              {notificationSettings.map(({ api, value, display }) => (
                <Box key={`${api}-container`} direction="row" gap="1rem" align="center">
                  <Box width={{ min: '4rem' }}>
                    <RadioToggleButton
                      key={uniqid()}
                      nameKey={api}
                      activeBg={button.selectBg}
                      inactiveBg={button.selectBg}
                      toggleBg={useToggleHighlight}
                      updateParent={(val) => handleSettingUpdate(api, val)}
                      initialValue={value}
                      isDisabled={paymentPending}
                    />
                  </Box>
                  <Text size="1rem" color={page.primaryText} weight={500}>
                    {settingsDisplayMap[api] || display}
                  </Text>
                </Box>
              ))}
            </Box>
          </SectionContainer>
        )}
        <SectionContainer
          small={small}
          sectionTitle="Profile"
          textColor={page.primaryText}
          sectionBg={useSectionBg}
          margin={{ bottom: '2rem' }}
        >
          <Box direction="column" gap="0.85rem">
            <ProfileLogo
              small={small}
              logoUrl={companyLogo}
              placeholder={accountPlaceholder}
              outlineColor={page.highlight}
              customSize={small ? '7rem' : '6rem'}
            />
            <Button plain disabled={paymentPending} onClick={() => logoUploadRef.current.click()} style={{ maxWidth: 'fit-content' }}>
              <Text weight={500} size="0.9rem" color={page.altHighlight}>Upload photo</Text>
            </Button>
          </Box>
          <Box direction="column" gap="1.35rem">
            <Text size="1rem">This is your registered email, you may reset your password by clicking the link below and following the instructions you&apos;ll recieve by email</Text>
            <StyledAccountInfoContainer focusHighlight={page.focusHighlight} width={{ width: small ? '100%' : '40%' }}>
              <Text size="1rem" color={page.primaryText} weight={500}>
                {companyEmail}
              </Text>
            </StyledAccountInfoContainer>
            <Box>
              <StyledAccountInfoContainer focusHighlight={page.focusHighlight} width={{ width: small ? '100%' : '40%' }}>
                <Text size="1rem" color={page.primaryText} weight={500}>
                  {replaceWithDots(uniqid())}
                </Text>
              </StyledAccountInfoContainer>
              <Box pad={{ top: '0.5rem' }}>
                <Button plain onClick={() => sendResetInstructions(companyEmail, paths.account)} style={{ maxWidth: 'fit-content' }}>
                  <Text weight={500} size="0.9rem" color={page.altHighlight}>Reset password</Text>
                </Button>
              </Box>
            </Box>
          </Box>
        </SectionContainer>
        {flags?.contentIdEnabled && (
          <SectionContainer
            small={small}
            sectionTitle="Business"
            textColor={page.primaryText}
            sectionBg={useSectionBg}
          >
            <Text size="1rem" color={page.primaryText}>
              This information is used to set up your payout account with Stripe.
              Please ensure your country and business type are correct before initiating payouts.
            </Text>
            <Box direction="column" gap="1rem">
              <Text size="1rem" color={page.primaryText} weight={600}>Country</Text>
              <Box width={small ? '100%' : '50%'}>
                <ProductFormDropdownSelect
                  searchable
                  keepLabel
                  selectable
                  simpleOptions={COUNTRIES}
                  handleFormValues={(val) => setBusinessCountry(val)}
                  value={businessCountry}
                  label={businessCountry}
                  placeholder="Select your country"
                  focusHighlight={page.focusHighlight}
                />
              </Box>
            </Box>
            <Box direction="column" gap="1rem">
              <Text size="1rem" color={page.primaryText} weight={600}>Business type</Text>
              <Box direction={small ? 'column' : 'row'} gap="1rem">
                {BUSINESS_TYPE_OPTIONS.map((option) => {
                  const active = businessType === option.key;
                  return (
                    <StyledAccountTypeOption
                      key={option.key}
                      active={active}
                      highlightColor={button.primaryBg}
                      hoverBg={button.selectBg}
                      tabIndex={0}
                      onClick={() => setBusinessType(option.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setBusinessType(option.key);
                      }}
                      flex="grow"
                      round="var(--app-radius)"
                      pad="0.65rem"
                      basis={small ? '100%' : '48%'}
                    >
                      <Box direction="row" align="center" gap="1rem">
                        <AppRadioButton
                          name="business-type"
                          checked={active}
                          onChange={() => setBusinessType(option.key)}
                          color={page.primaryText}
                          highlightColor={button.primaryBg}
                          borderColor={active ? button.primaryBg : button.border}
                          label={null}
                          customCircleSize={small ? '18px' : '24px'}
                        />
                        <Text size="1.1rem" weight={600} color={page.primaryText}>
                          {option.label}
                        </Text>
                      </Box>
                    </StyledAccountTypeOption>
                  );
                })}
              </Box>
            </Box>
            <Box>
              <AppButton
                overrideHover
                level="authSubmit"
                bgColor={button.primaryBg}
                color={button.primaryText}
                label={businessSaving ? 'Saving…' : 'Save business info'}
                alignSelf="start"
                disabled={
                  businessSaving
                  || !businessCountry
                  || !businessType
                  || (businessCountry === savedBusinessCountry
                    && businessType === savedBusinessType)
                }
                onClick={async () => {
                  setBusinessSaving(true);
                  await saveBusinessInfo(businessCountry, businessType, () => {
                    setSavedBusinessCountry(businessCountry);
                    setSavedBusinessType(businessType);
                  });
                  setBusinessSaving(false);
                }}
              />
            </Box>
          </SectionContainer>
        )}
      </Box>
    </PageWrapper>
  );
};

function mapStateToProps(state) {
  return {
    companyEmail: state.auth.email,
    companyLogo: state.auth.companyLogo,
    companyId: state.auth.companyId,
    companyOnboardingId: state.auth.onboardingId,
    loading: state.fetchLoader.dataLoading,
    secondaryLoading: state.payment.customerOnboardingDataLoading,
    subscriptionData: state.payment.subscriptionData,
    cancellationRequestedAt: state.payment.cancellationRequestedAt,
    notificationSettings: state.companySettings.companyNotificationSettings,
    onboardingData: state.payment.customerOnboardingData,
    payoutAccount: state.payment.payoutAccount,
    payoutAccountLoading: state.payment.payoutAccountLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    sendResetInstructions: sendPasswordResetInstructionsRequest,
    uploadCompanyLogo: authUploadCompanyLogoRequest,
    fetchSubscription: fetchCustomerSubscriptionRequest,
    fetchCancellation: fetchCancellationStatus,
    clearSubscription: clearCustomerSubscriptionRequest,
    fetchNotificationSettings: initiateCompanyNotificationSettingRequest,
    clearNotificationSettings: clearCompanyNotificationSettingRequest,
    updateNotificationSetting: updateCompanyNotificationSettingRequest,
    fetchOnboardingData: fetchCustomerOnboardingData,
    fetchAccount: fetchPayoutAccount,
    clearAccount: clearPayoutAccount,
    saveBusinessInfo: updatePayoutBusinessInfo,
    loadBusinessInfo: fetchPayoutBusinessInfo,
  }, dispatch);
}

AccountPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  unconfiguredCompany: PropTypes.bool,
  cancellationRequestedAt: PropTypes.string,
  companyEmail: PropTypes.string.isRequired,
  companyId: PropTypes.number.isRequired,
  companyLogo: PropTypes.string,
  sendResetInstructions: PropTypes.func.isRequired,
  uploadCompanyLogo: PropTypes.func.isRequired,
  fetchSubscription: PropTypes.func.isRequired,
  fetchCancellation: PropTypes.func.isRequired,
  clearSubscription: PropTypes.func.isRequired,
  fetchNotificationSettings: PropTypes.func.isRequired,
  clearNotificationSettings: PropTypes.func.isRequired,
  updateNotificationSetting: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  secondaryLoading: PropTypes.bool.isRequired,
  subscriptionData: PropTypes.shape({
    status: PropTypes.string.isRequired,
    productData: PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      metadata: PropTypes.shape({
        searchLimit: PropTypes.string.isRequired,
        uid: PropTypes.string.isRequired,
        verticalUid: PropTypes.string.isRequired,
      }).isRequired,
      prices: PropTypes.arrayOf(PropTypes.shape({
        priceId: PropTypes.string.isRequired,
        currency: PropTypes.string.isRequired,
        unitAmount: PropTypes.number.isRequired,
        metadata: PropTypes.shape({
          order: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          displayPlatforms: PropTypes.string.isRequired,
          platforms: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        }).isRequired,
      })).isRequired,
    }).isRequired,
    cardData: PropTypes.shape({
      brand: PropTypes.string.isRequired,
      last4: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      expMonth: PropTypes.number.isRequired,
      expYear: PropTypes.number.isRequired,
    }),
    createdAt: PropTypes.number.isRequired,
    currentPeriodEnd: PropTypes.number.isRequired,
    currentPeriodStart: PropTypes.number.isRequired,
    canceledDate: PropTypes.number,
    willCancelDate: PropTypes.number,
  }),
  notificationSettings: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
    value: PropTypes.bool.isRequired,
  }).isRequired),
  flags: PropTypes.objectOf(PropTypes.any),
  paymentPending: PropTypes.bool.isRequired,
  companyOnboardingId: PropTypes.string,
  fetchOnboardingData: PropTypes.func.isRequired,
  fetchAccount: PropTypes.func.isRequired,
  clearAccount: PropTypes.func.isRequired,
  saveBusinessInfo: PropTypes.func.isRequired,
  loadBusinessInfo: PropTypes.func.isRequired,
  onboardingData: PropTypes.shape({
    onboardingId: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    paymentPending: PropTypes.bool.isRequired,
    paymentLink: PropTypes.string,
    youtubeCid: PropTypes.shape({
      uploader: PropTypes.string,
      plan: PropTypes.string,
    }),
  }),
  payoutAccount: PropTypes.shape({
    email: PropTypes.string,
  }),
  payoutAccountLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(AccountPage));
